import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  supplierCatalogItemListPageQuery,
  supplierCatalogItemDelete as supplierCatalogItemDeleteMutation,
} from '../graphql/supplier_catalog_item_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const SupplierCatalogItemList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [supplierCatalogItemDelete] = useMutation(supplierCatalogItemDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(supplierCatalogItemListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ supplierCatalogItemList }) => {
      // write an individual query for every supplierCatalogItem in the list
      updateFunctions.supplierCatalogItemList(pageClient, supplierCatalogItemList);
    },
  });

  const currencyFormatter = new Intl.NumberFormat('en-NZ', {
    style: 'decimal',
    minimumFractionDigits: 2,
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const supplierCatalogItemDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const supplierCatalogItemId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: supplierCatalogItemId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'SupplierCatalogItemType',
        recordId: supplierCatalogItemId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.supplierCatalogItemDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'supplierCatalogItemDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await supplierCatalogItemDelete(mutationData);
        toastSuccess('Supplier Catalog Item delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Supplier Catalog Item delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      supplierCatalogItemDelete(mutationData);
      toastWarning(
        `Supplier Catalog Item delete ok locally. Go online to make permanent`
      );
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const getSupplierName = (row) => {
    const {
      supplierCatalog: {
        supplier: { name: supplierName },
      },
    } = row;
    return supplierName;
  };

  const getSupplierCatalogName = (row) => {
    const {
      supplierCatalog: { name: supplierCatalogName },
    } = row;
    return supplierCatalogName;
  };

  const getManufacturerName = (row) => {
    const {
      manufacturer: { name: manufacturerName },
    } = row;
    return manufacturerName;
  };

  const renderCurrencyCell = (cell) => (
    <span>{currencyFormatter.format(cell.getValue())}</span>
  );

  const parentColumns = [
    {
      header: 'Supplier',
      id: 'supplier',
      accessorFn: (row) => getSupplierName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Supplier Catalog',
      id: 'supplierCatalog',
      accessorFn: (row) => getSupplierCatalogName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Manufacturer',
      id: 'manufacturer',
      accessorFn: (row) => getManufacturerName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Model/Part #',
      accessorKey: 'modelPartNumber',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Description',
      accessorKey: 'description',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Unit of Measure',
      accessorKey: 'catalogItemUnitOfMeasure',
      filterType: 'dropdown',
    },
    {
      header: 'Qty Unit of Measure',
      accessorKey: 'catalogItemQuantityPerUnitOfMeasure',
      enableColumnFilter: false,
      cellClassName: 'text-end',
    },
    // {
    //   header: 'Currency',
    //   accessorKey: 'catalogItemCurrency',
    //   filterType: 'dropdown',
    // },
    // {
    //   header: 'Unit Price',
    //   accessorKey: 'catalogItemUnitPrice',
    //   cellClassName: 'text-end',
    //   cell: renderCurrencyCell,
    //   enableColumnFilter: true,
    //   filterFn: 'includesString',
    // },
    // {
    //   header: 'Price Unit of Measure',
    //   accessorKey: 'catalogItemPricePerUnitOfMeasure',
    //   cellClassName: 'text-end',
    //   cell: renderCurrencyCell,
    //   enableColumnFilter: true,
    //   filterFn: 'includesString',
    // },
    // {
    //   header: 'Imported Merged Price',
    //   accessorKey: 'catalogItemImportedMergedPrice',
    //   cellClassName: 'text-end',
    //   cell: renderCurrencyCell,
    //   enableColumnFilter: true,
    //   filterFn: 'includesString',
    // },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Supplier Catalog Items</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/supplier_catalog_items/new">
                <Button variant="primary">New Supplier Catalog Item</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="supplierCatalogItem"
                parentColumns={parentColumns}
                data={pageData.supplierCatalogItemList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={supplierCatalogItemDeleteClicked}
                pageSizes={[50, 100, 200]}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default SupplierCatalogItemList;
